<template>
  <div ref="root" class="markdown-editor">
    <div class="panel">
      <el-dropdown>
        <span class="el-dropdown-link">
          <el-button :icon="header" type="text" :disabled="!toggle" />
          <el-icon class="el-icon--right">
            <ArrowDown />
          </el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu :style="!toggle && {display: 'none'}">
            <el-dropdown-item
              v-for="item in headers"
              :key="item.label"
              @click="item.fn()"
            >
              {{ item.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-divider direction="vertical" />
      <el-tooltip
        class="box-item"
        content="Жирный"
        placement="bottom"
        :show-after="1000"
      >
        <el-button
          :icon="bold"
          type="text"
          :disabled="!toggle"
          @mousedown.prevent
          @click="toggleInline('**')"
        />
      </el-tooltip>
      <el-tooltip
        class="box-item"
        content="Курсив"
        placement="bottom"
        :show-after="1000"
      >
        <el-button
          :icon="italic"
          type="text"
          :disabled="!toggle"
          @mousedown.prevent
          @click="toggleInline('*')"
        />
      </el-tooltip>
      <el-tooltip
        class="box-item"
        content="Зачеркнутый"
        placement="bottom"
        :show-after="1000"
      >
        <el-button
          :icon="strike"
          type="text"
          :disabled="!toggle"
          @mousedown.prevent
          @click="toggleInline('~~')"
        />
      </el-tooltip>
      <el-tooltip
        class="box-item"
        content="Цитата"
        placement="bottom"
        :show-after="1000"
      >
        <el-button
          :icon="quote"
          type="text"
          :disabled="!toggle"
          @mousedown.prevent
          @click="toggleBlock('> ', QUOTE_PATTERN)"
        />
      </el-tooltip>
      <el-tooltip
        class="box-item"
        content="Маркированный список"
        placement="bottom"
        :show-after="1000"
      >
        <el-button
          :icon="listUl"
          type="text"
          :disabled="!toggle"
          @mousedown.prevent
          @click="toggleBlock('* ', LIST_PATTERN)"
        />
      </el-tooltip>
      <el-tooltip
        class="box-item"
        content="Отступ в начале строки"
        placement="bottom"
        :show-after="1000"
      >
        <el-button
          :icon="indentSvg"
          type="text"
          :disabled="!toggle"
          @mousedown.prevent
          @click="insertIndent"
        />
      </el-tooltip>
      <el-tooltip
        class="box-item"
        content="Ссылка"
        placement="bottom"
        :show-after="1000"
      >
        <el-dropdown
          ref="linkdropdown"
          :hide-on-click="false"
          trigger="click"
          @visible-change="onLinkDropdownToggle"
        >
          <span class="el-dropdown-link">
            <el-button :icon="linkSvg" type="text" :disabled="!toggle" />
          </span>
          <template #dropdown>
            <div style="padding: 10px">
              <el-form label-position="top" inline>
                <el-form-item style="margin: 0 10px" label="Текст" size="small">
                  <el-input
                    v-model="modelLinkName"
                  />
                </el-form-item>
                <el-form-item
                  style="margin: 0 10px"
                  required
                  label="Ссылка"
                  size="small"
                >
                  <el-input
                    v-model="modelLink"
                    placeholder="https://"
                  />
                </el-form-item>
                <el-form-item style="margin: 0 10px" label="&nbsp;">
                  <el-button
                    :disabled="!modelLink"
                    type="primary"
                    size="small"
                    @click="insertLink"
                  >
                    Добавить
                  </el-button>
                </el-form-item>
              </el-form>
            </div>
          </template>
        </el-dropdown>
      </el-tooltip>
      <el-divider direction="vertical" />
      <div v-if="!toggle">
        <el-tooltip
          class="box-item"
          content="Редактирование"
          placement="bottom"
          :show-after="1000"
        >
          <el-button
            :icon="edit"
            type="text"
            @click.prevent="toggle = !toggle"
          />
        </el-tooltip>
      </div>
      <div v-else>
        <el-tooltip
          class="box-item"
          content="Просмотр"
          placement="bottom"
          :show-after="1000"
        >
          <el-button
            :icon="eye"
            type="text"
            :disabled="input.length === 0"
            @click.prevent="toggle = !toggle"
          />
        </el-tooltip>
      </div>
    </div>
    <MagnerFormTextarea
      v-if="toggle"
      v-model="input"
      :field="field"
    />
    <div v-else class="markdown-editor__preview" v-html="resultantMarkdown" />
  </div>
</template>

<script lang="ts" setup>
import {
  computed, nextTick, ref, watch,
} from 'vue';
import { MagnerFormTextarea, TextareaField } from 'magner';
import { ArrowDown } from '@element-plus/icons-vue';
import { marked } from '../../../../../public/scripts/marked.esm.js';
import header from './assets/icons/h.svg';
import bold from './assets/icons/bold.svg';
import eye from './assets/icons/eye.svg';
import edit from './assets/icons/edit.svg';
import italic from './assets/icons/italic.svg';
import strike from './assets/icons/strike.svg';
import quote from './assets/icons/quote.svg';
import listUl from './assets/icons/list-ul.svg';
import linkSvg from './assets/icons/link.svg';
import indentSvg from './assets/icons/indent.svg';

interface Props {
  modelValue: string;
  field: TextareaField<any>;
}
const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue']);

const root = ref<HTMLElement>();
const linkdropdown = ref();
const modelLink = ref('');
const modelLinkName = ref('');
const toggle = ref(true);

const HEADING_PATTERN = /^#{1,6}\s*/;
const QUOTE_PATTERN = /^>\s?/;
const LIST_PATTERN = /^[-*+]\s+/;

/**
 * Markdown не сохраняет ни лишние пустые строки, ни отступы в начале строки:
 * несколько пустых строк схлопываются в один разрыв абзаца, а ведущие пробелы
 * срезает парсер — и marked в вебе, и flutter_markdown в мобильном приложении.
 * Поэтому в textarea пользователь работает обычными Enter и пробелами, а наружу
 * они уезжают невидимыми символами: zero-width space пробелом не считается и
 * «держит» строку, а идущие за ним неразрывные пробелы дают ту же ширину, что и обычные.
 * Символы, а не HTML-entity: описание события уходит ещё и в календарь (Event.description
 * в мобильном приложении), где markdown не рендерится и «&#8203;» был бы виден текстом.
 */
const ZERO_WIDTH_SPACE = '\u200B';
const NO_BREAK_SPACE = '\u00A0';
const TAB_WIDTH = 4;
const INDENT_WIDTH = 2;

/* entity — формат прежних записей, читаем его наравне с символами */
const EMPTY_LINES_PATTERN = /\n\n(?:(?:\u200B|&#8203;)\n\n)+/g;
const SPACER_PATTERN = /\u200B|&#8203;/g;
const INDENT_PATTERN = /^(?:\u200B|&#8203;)?((?:\u00A0|&nbsp;|&ensp;)+) ?/gm;
const INDENT_UNIT_PATTERN = /\u00A0|&nbsp;|&ensp;/g;

/**
 * Текст из редактора → markdown для хранения.
 * Каждая пустая строка редактора становится отдельным абзацем из невидимого символа:
 * иначе markdown отдаст просто разрыв абзаца, и в превью с приложением пустой строки не будет.
 */
const encode = (value: string) => value
  .replace(
    /\n{2,}/g,
    (breaks) => `\n\n${`${ZERO_WIDTH_SPACE}\n\n`.repeat(breaks.length - 1)}`,
  )
  /* (?![ \t]) не даёт откатиться на часть отступа: иначе разметка вложенного списка,
     который отступ как раз задаёт, распалась бы на невидимые пробелы */
  .replace(
    /^[ \t]+(?![ \t])(?![-*+>]\s|\d+[.)]\s)/gm,
    (indent) => ZERO_WIDTH_SPACE
      + NO_BREAK_SPACE.repeat(indent.replace(/\t/g, ' '.repeat(TAB_WIDTH)).length),
  );

/** Хранимый markdown → текст для редактирования */
const decode = (value: string) => value
  .replace(
    EMPTY_LINES_PATTERN,
    (block) => '\n'.repeat((block.match(SPACER_PATTERN) as string[]).length + 1),
  )
  .replace(
    INDENT_PATTERN,
    (_, indent: string) => ' '.repeat((indent.match(INDENT_UNIT_PATTERN) as string[]).length),
  );

/** В textarea живёт «человеческий» текст, наружу отдаётся закодированный markdown */
const input = ref(decode(props.modelValue));

/**
 * Сырой HTML в мобильном приложении выводится как обычный текст,
 * поэтому в превью его тоже не исполняем: опция sanitize удалена из marked с 5-й версии.
 * Экранируем только «<» — «>» нужен markdown-у для цитат.
 */
const resultantMarkdown = computed(
  () => marked(encode(input.value).replace(/</g, '&lt;'), { breaks: true }),
);

/** Ищем textarea только внутри своего компонента, а не первый попавшийся в документе */
const getTextarea = (): HTMLTextAreaElement | null => root.value?.querySelector('textarea') ?? null;

/**
 * Заменяет фрагмент [from, to) на text и восстанавливает выделение.
 * Пишем через execCommand: он сохраняет нативную историю отмены (Ctrl+Z)
 * и сам инициирует событие input, благодаря чему обновляется v-model.
 */
async function applyChange (
  textarea: HTMLTextAreaElement,
  from: number,
  to: number,
  text: string,
  selectionStart: number,
  selectionEnd: number = selectionStart,
) {
  if (from === to && !text) return;

  textarea.focus();
  textarea.setSelectionRange(from, to);

  const applied = text
    ? document.execCommand('insertText', false, text)
    : document.execCommand('delete');

  if (!applied) {
    textarea.setRangeText(text, from, to, 'end');
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  await nextTick();
  textarea.focus();
  textarea.setSelectionRange(selectionStart, selectionEnd);
}

/** Маркер стоит внутри выделения: «**текст**» */
const isWrapped = (selected: string, marker: string): boolean => {
  if (selected.length < marker.length * 2) return false;
  if (!selected.startsWith(marker) || !selected.endsWith(marker)) return false;
  /* одиночная «*» не должна принимать за курсив половину жирного «**» */
  if (marker.length === 1
    && (selected[1] === marker || selected[selected.length - 2] === marker)) return false;
  return true;
};

/** Маркер стоит вокруг выделения: «**[текст]**» */
const isSurrounded = (value: string, start: number, end: number, marker: string): boolean => {
  const len = marker.length;
  if (start < len) return false;
  if (value.slice(start - len, start) !== marker || value.slice(end, end + len) !== marker) return false;
  if (len === 1 && (value[start - 2] === marker || value[end + 1] === marker)) return false;
  return true;
};

/** Жирный / курсив / зачёркнутый: оборачивает выделение, повторное нажатие снимает разметку */
function toggleInline (marker: string) {
  const textarea = getTextarea();
  if (!textarea) return;

  const { value } = textarea;
  let start = textarea.selectionStart;
  let end = textarea.selectionEnd;

  /* пробелы по краям выносим за маркеры — иначе разметка не сработает */
  while (start < end && /\s/.test(value[start])) start += 1;
  while (end > start && /\s/.test(value[end - 1])) end -= 1;

  const selected = value.slice(start, end);
  const len = marker.length;

  if (isWrapped(selected, marker)) {
    const unwrapped = selected.slice(len, -len);
    applyChange(textarea, start, end, unwrapped, start, start + unwrapped.length);
    return;
  }

  if (isSurrounded(value, start, end, marker)) {
    applyChange(textarea, start - len, end + len, selected, start - len, start - len + selected.length);
    return;
  }

  const caret = start + len;
  applyChange(
    textarea,
    start,
    end,
    `${marker}${selected}${marker}`,
    caret,
    selected ? caret + selected.length : caret,
  );
}

/**
 * Заголовки, цитата, список: префикс ставится в начало каждой строки выделения.
 * Если он уже стоит на всех строках — снимается. Пустой префикс просто очищает разметку.
 */
function toggleBlock (prefix: string, pattern: RegExp) {
  const textarea = getTextarea();
  if (!textarea) return;

  const {
    value,
    selectionStart: start,
    selectionEnd: end,
  } = textarea;
  const blockStart = value.lastIndexOf('\n', start - 1) + 1;
  const lineBreak = value.indexOf('\n', end);
  const blockEnd = lineBreak === -1 ? value.length : lineBreak;

  const lines = value.slice(blockStart, blockEnd).split('\n');
  const applied = Boolean(prefix) && lines.every((line) => !line.trim() || line.startsWith(prefix));

  const updatedLines = lines.map((line) => {
    const clean = line.replace(pattern, '');
    return !prefix || applied ? clean : `${prefix}${clean}`;
  });
  const updated = updatedLines.join('\n');

  if (start === end) {
    const caret = Math.max(blockStart, start + updatedLines[0].length - lines[0].length);
    applyChange(textarea, blockStart, blockEnd, updated, caret);
    return;
  }

  applyChange(textarea, blockStart, blockEnd, updated, blockStart, blockStart + updated.length);
}

/** Простая вставка перед выделением, чтобы не затирать текст */
function insertAtCaret (markdown: string) {
  const textarea = getTextarea();
  if (!textarea) return;

  const { selectionStart: caret } = textarea;
  applyChange(textarea, caret, caret, markdown, caret + markdown.length);
}

/** Отступ в начало текущей строки — Tab в textarea увёл бы фокус */
function insertIndent () {
  const textarea = getTextarea();
  if (!textarea) return;

  const { value, selectionStart: caret } = textarea;
  const lineStart = value.lastIndexOf('\n', caret - 1) + 1;
  const indent = ' '.repeat(INDENT_WIDTH);

  applyChange(textarea, lineStart, lineStart, indent, caret + INDENT_WIDTH);
}

/** Выделенный текст подставляем в поле «Текст» ссылки */
function onLinkDropdownToggle (visible: boolean) {
  if (!visible) return;

  const textarea = getTextarea();
  if (!textarea) return;

  modelLinkName.value = textarea.value.slice(textarea.selectionStart, textarea.selectionEnd).trim();
}

function insertLink () {
  const textarea = getTextarea();
  if (!textarea) return;

  const { selectionStart: start, selectionEnd: end } = textarea;
  const label = modelLinkName.value || textarea.value.slice(start, end) || modelLink.value;
  const link = `[${label}](${modelLink.value})`;

  applyChange(textarea, start, end, link, start + link.length);
  modelLink.value = '';
  modelLinkName.value = '';
  linkdropdown.value?.handleClose();
}

const headers = [
  {
    label: 'Обычный текст',
    fn: () => toggleBlock('', HEADING_PATTERN),
  },
  {
    label: 'Заголовок 1',
    fn: () => toggleBlock('# ', HEADING_PATTERN),
  },
  {
    label: 'Заголовок 2',
    fn: () => toggleBlock('## ', HEADING_PATTERN),
  },
  {
    label: 'Заголовок 3',
    fn: () => toggleBlock('### ', HEADING_PATTERN),
  },
];

watch(() => input.value, (value) => {
  emit('update:modelValue', encode(value));
});

watch(() => props.modelValue, (value) => {
  if (value !== encode(input.value)) input.value = decode(value);
});
</script>

<style scoped>
.markdown-editor {
  width: 100%;
}
.panel {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel button {
  width: auto;
  margin: 0;
}
.el-dropdown-link {
  display: flex;
  align-items: baseline;
}
.el-dropdown-menu :nth-child(2) {
  font-size: 18px;
  font-weight: 600;
}
.el-dropdown-menu :nth-child(3) {
  font-size: 16px;
  font-weight: 600;
}
.el-dropdown-menu :nth-child(4) {
  font-weight: 600;
}
/**
 * Превью повторяет метрики мобильного приложения (MarkdownStyleSheet из flutter_markdown
 * + тема из common/lib/utils/app_theme.dart), чтобы в админке и в МП текст выглядел одинаково:
 * blockSpacing 8, listIndent 24, p = bodyLarge 15/1.2,
 * h1 = headlineSmall 17 w600, h2 = titleLarge 22 w400, h3 = titleMedium 15 w600.
 */
.markdown-editor__preview {
  font-size: 15px;
  line-height: 1.2;
}
.markdown-editor__preview :deep(p),
.markdown-editor__preview :deep(h1),
.markdown-editor__preview :deep(h2),
.markdown-editor__preview :deep(h3),
.markdown-editor__preview :deep(ul),
.markdown-editor__preview :deep(ol),
.markdown-editor__preview :deep(blockquote) {
  margin: 8px 0 0;
}
.markdown-editor__preview :deep(> :first-child) {
  margin-top: 0;
}
.markdown-editor__preview :deep(h1) {
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
}
.markdown-editor__preview :deep(h2) {
  font-size: 22px;
  font-weight: 400;
}
.markdown-editor__preview :deep(h3) {
  font-size: 15px;
  font-weight: 600;
}
.markdown-editor__preview :deep(ul),
.markdown-editor__preview :deep(ol) {
  padding-left: 24px;
}
.markdown-editor__preview :deep(blockquote) {
  padding: 8px;
  border-radius: 2px;
  /* дефолт flutter_markdown (Colors.blue.shade100), тема приложения его не переопределяет */
  background: #bbdefb;
}
</style>
